import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CaretUp, CaretDown } from '@phosphor-icons/react';

export const NexusTable = ({
  columns,
  data,
  onRowClick,
  sortable = true,
  loading = false,
  emptyMessage = 'No data available',
  className = '',
}) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  
  const handleSort = (key) => {
    if (!sortable) return;
    
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };
  
  const sortedData = [...data].sort((a, b) => {
    if (!sortConfig.key) return 0;
    
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];
    
    if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });
  
  return (
    <div className={`w-full overflow-x-auto ${className}`}>
      <table className="w-full border-collapse">
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                onClick={() => column.sortable !== false && handleSort(column.key)}
                className={`
                  bg-nexus-bg-secondary text-left text-xs font-bold
                  text-nexus-text-tertiary uppercase tracking-widest
                  px-4 py-3 border-b border-white/10
                  ${sortable && column.sortable !== false ? 'cursor-pointer hover:text-nexus-text-secondary' : ''}
                  ${column.className || ''}
                `}
              >
                <div className="flex items-center gap-2">
                  {column.header}
                  {sortable && column.sortable !== false && sortConfig.key === column.key && (
                    <motion.span
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      {sortConfig.direction === 'asc' ? (
                        <CaretUp className="w-3 h-3" weight="bold" />
                      ) : (
                        <CaretDown className="w-3 h-3" weight="bold" />
                      )}
                    </motion.span>
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <AnimatePresence>
            {loading ? (
              // Skeleton rows
              [...Array(5)].map((_, i) => (
                <tr key={i} className="border-b border-white/5">
                  {columns.map((column) => (
                    <td key={column.key} className="px-4 py-3">
                      <div className="h-4 bg-nexus-bg-tertiary rounded animate-shimmer" />
                    </td>
                  ))}
                </tr>
              ))
            ) : sortedData.length === 0 ? (
              <tr>
                <td 
                  colSpan={columns.length} 
                  className="px-4 py-8 text-center text-nexus-text-tertiary"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              sortedData.map((row, rowIndex) => (
                <motion.tr
                  key={row.id || rowIndex}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: rowIndex * 0.05 }}
                  onClick={() => onRowClick?.(row)}
                  className={`
                    border-b border-white/5
                    transition-colors duration-150
                    ${onRowClick ? 'cursor-pointer hover:bg-nexus-bg-tertiary/50' : ''}
                  `}
                >
                  {columns.map((column) => (
                    <td 
                      key={column.key} 
                      className={`px-4 py-3 text-sm text-nexus-text-primary ${column.cellClassName || ''}`}
                    >
                      {column.render ? column.render(row[column.key], row) : row[column.key]}
                    </td>
                  ))}
                </motion.tr>
              ))
            )}
          </AnimatePresence>
        </tbody>
      </table>
    </div>
  );
};

export const NexusTablePagination = ({
  currentPage,
  totalPages,
  onPageChange,
  className = '',
}) => {
  const pages = [];
  const maxVisible = 5;
  
  let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  let end = Math.min(totalPages, start + maxVisible - 1);
  
  if (end - start + 1 < maxVisible) {
    start = Math.max(1, end - maxVisible + 1);
  }
  
  for (let i = start; i <= end; i++) {
    pages.push(i);
  }
  
  return (
    <div className={`flex items-center justify-center gap-1 ${className}`}>
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="px-3 py-1.5 text-sm text-nexus-text-secondary hover:text-nexus-text-primary disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Prev
      </button>
      
      {start > 1 && (
        <>
          <button
            onClick={() => onPageChange(1)}
            className="px-3 py-1.5 text-sm text-nexus-text-secondary hover:text-nexus-text-primary"
          >
            1
          </button>
          {start > 2 && <span className="text-nexus-text-tertiary">...</span>}
        </>
      )}
      
      {pages.map(page => (
        <motion.button
          key={page}
          onClick={() => onPageChange(page)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          className={`
            px-3 py-1.5 text-sm rounded transition-colors
            ${currentPage === page 
              ? 'bg-nexus-accent-mint text-nexus-bg-primary font-medium' 
              : 'text-nexus-text-secondary hover:text-nexus-text-primary'
            }
          `}
        >
          {page}
        </motion.button>
      ))}
      
      {end < totalPages && (
        <>
          {end < totalPages - 1 && <span className="text-nexus-text-tertiary">...</span>}
          <button
            onClick={() => onPageChange(totalPages)}
            className="px-3 py-1.5 text-sm text-nexus-text-secondary hover:text-nexus-text-primary"
          >
            {totalPages}
          </button>
        </>
      )}
      
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="px-3 py-1.5 text-sm text-nexus-text-secondary hover:text-nexus-text-primary disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Next
      </button>
    </div>
  );
};
